import { useEffect, useState } from 'react';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { useStore } from 'src/contexts/StoreContext';
import {
  checkScopes,
  deleteAccessToken,
  getAccountInfo,
  getMediaItems,
  isAccessTokenValid,
  pollSession,
  readAccessToken,
  startSession,
} from 'src/helpers/google.helper';
import { usePolling } from 'src/hooks/usePolling';
import { logError, logWarning } from 'src/_shared/logger';
import { openWindow } from 'src/_shared/utils/open-window/openWindow';
import { useSizeLimitContext } from 'src/contexts/SizeLimitContext';
import { EDITOR_BASE_URL } from 'src/app/settings';
import { ROUTE_PATHS } from 'src/routes/_shared/routes.constants';
import { convertObjectToQueryString } from 'src/_shared/utils/convertObjectToQueryString';
import { SCOPE_GOOGLE_PHOTO } from 'src/constants/google.constants';
import { useRoute } from '@react-navigation/native';
import { SELECTED_MENU_ITEMS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { SourcesEventType } from '@photosi/rn-components/modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import { filesToPickedImages } from 'src/_shared/utils/filesToPickedImages';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';

type Params = {
  onConfirmStep: (error?: boolean, productImages?: PickedImage[], googleAccessToken?: string) => void;
  setUploadingPhoto?: (uploading: boolean) => void;
  onLoadingChange?: (loading: boolean) => void;
};

export const usePickerGoogle = ({ onConfirmStep, setUploadingPhoto, onLoadingChange }: Params) => {
  const {
    i18n,
    dynamicModal: { closeModal },
    toastStore,
    project: { activeProject, activeGroup, replaceUserImage },
    googleAccount,
    data: { isReplacingPhoto },
  } = useStore();

  const {
    email,
    profilePic,
    isUserLogged,
    accessToken,
    setIsUserLogged,
    setEmail,
    setProfilePic,
    setAccessToken,
    setHasGivenAccessToPhoto,
    hasGivenAccessToPhoto,
  } = googleAccount;

  const { isDesktop } = useResponsiveScreen();
  const screenSize = useSizeLimitContext();
  const route = useRoute();

  const [sessionId, setSessionId] = useState('');
  const [mediaItemsSet, setMediaItemsSet] = useState(false);
  const [isLoadingLogin, setLoadingLogin] = useState(false);

  const { startPolling, stopPolling } = usePolling();
  const { startPolling: startPollingSession, stopPolling: stopPollingSession } = usePolling();

  const notifyLoading = (loading: boolean) => {
    setLoadingLogin(loading);
    onLoadingChange?.(loading);
  };

  const handleLogin = async () => {
    notifyLoading(true);
    amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachClick, {
      source_type: SourcesEventType.GooglePhoto,
    });

    const popup = await openWindow(
      `${EDITOR_BASE_URL}/${ROUTE_PATHS.GOOGLE_LOGIN}?${convertObjectToQueryString(route.params)}`,
      {
        target: 'null',
        dimensions: {
          width: (screenSize.width * 3) / 10,
          height: (screenSize.height * 6) / 10,
          left: (screenSize.width * 4) / 10,
          top: (screenSize.height * 2) / 10,
        },
      },
    );

    startPolling(async () => {
      const token = readAccessToken();

      if (popup?.closed && !token) {
        stopPolling();
        notifyLoading(false);
      }

      if (token) {
        try {
          const account = await getAccountInfo({ accessToken: token });
          setProfilePic(account.picture);
          setEmail(account.email);

          const scopes = await checkScopes(token);
          const hasAccess = scopes.scope.includes(SCOPE_GOOGLE_PHOTO);
          setHasGivenAccessToPhoto(hasAccess);

          amplitude.trackEvent(
            hasAccess
              ? AnalyticsEvents.dynamicPicker.sourceAttachSuccess
              : AnalyticsEvents.dynamicPicker.sourceAttachError,
            { source_type: SourcesEventType.GooglePhoto },
          );

          setAccessToken(token);
          setIsUserLogged(true);
          stopPolling();
          notifyLoading(false);
        } catch (e) {
          logError(e);
          notifyLoading(false);
        }
      }
    });
  };

  const handleChoosePhoto = async () => {
    return startSession({ accessToken })
      .then(async res => {
        setSessionId(res.sessionId);

        startPollingSession(async () => {
          try {
            if (!isAccessTokenValid()) {
              stopPollingSession();
              handleLogout();
              return;
            }
            const completed = await pollSession({ sessionId: res.sessionId, accessToken });
            if (completed) {
              setMediaItemsSet(true);
              stopPollingSession();
            }
          } catch (e) {
            logError(e);
          }
        }, res.interval);

        await openWindow(
          res.pickerUri,
          isDesktop
            ? {
                target: 'null',
                dimensions: {
                  width: (screenSize.width * 6) / 10,
                  height: (screenSize.height * 6) / 10,
                  left: (screenSize.width * 2) / 10,
                  top: (screenSize.height * 2) / 10,
                },
              }
            : undefined,
        );
      })
      .catch(error => {
        logWarning('[Google Photo] - Choose Photo Error: ', error);
        setHasGivenAccessToPhoto(false);
      });
  };

  const handleLogout = () => {
    deleteAccessToken();
    setProfilePic('');
    setEmail('');
    setIsUserLogged(false);
    amplitude.trackEvent(AnalyticsEvents.photoSources.detachSourceClick, {
      source: SourcesEventType.GooglePhoto,
    });
  };

  useEffect(() => {
    const token = readAccessToken();
    if (!token) return;

    notifyLoading(true);
    setAccessToken(token);

    getAccountInfo({ accessToken: token }).then(res => {
      setProfilePic(res.picture);
      setEmail(res.email);
    });

    checkScopes(token).then(res => {
      setHasGivenAccessToPhoto(res.scope.includes(SCOPE_GOOGLE_PHOTO));
    });

    setIsUserLogged(true);
    notifyLoading(false);
  }, []);

  useEffect(() => {
    if (!mediaItemsSet) return;
    setUploadingPhoto?.(true);

    getMediaItems({ sessionId, accessToken }).then(async res => {
      const photos = res.filter(item => item.type === 'PHOTO');

      const pickedImages = await filesToPickedImages(
        photos,
        {
          i18n,
          errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
            if (areAllPhotosInvalid) closeModal();
            toastStore.showErrorToast({
              id: 'alert-uploading-photos-error-toast',
              message: errorMessage,
            });
          },
        },
        true,
      );

      pickedImages.forEach(p => (p.source = SELECTED_MENU_ITEMS.GooglePhoto));

      if (!isReplacingPhoto) {
        onConfirmStep(false, pickedImages, accessToken);
      } else {
        const image = activeGroup?.userImagesWithQualityAlert.find(e => e.id === isReplacingPhoto);
        image &&
          replaceUserImage({
            pickedImage: pickedImages[0],
            googleAccessToken: '',
            productId: image?.productId,
            userImageId: image?.id,
          });
      }

      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
        n_photo: pickedImages.length,
        source_type: SourcesEventType.GooglePhoto,
      });

      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.continueClick, {
        n_photo: pickedImages.length,
        n_photos_facebook: 0,
        n_photos_gphoto: pickedImages.length,
        n_photos_local: 0,
        n_photos_smartphone: 0,
        n_photos_family_friends: 0,
      });

      setUploadingPhoto?.(false);
    });
  }, [mediaItemsSet]);

  return {
    isDesktop,
    isLoadingLogin,
    isUserLogged,
    hasGivenAccessToPhoto,
    email,
    profilePic,
    handleLogin,
    handleChoosePhoto,
    handleLogout,
  };
};
